import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlayIcon, SearchIcon, SendIcon } from 'lucide-react';
import { Section } from '../Section';
import { InView } from '../InView';
import { SectionHeadline } from '../SectionHeadline';
import { SnakStill } from '../../SnakStill';
import { WalkthroughInvite } from '../WalkthroughInvite';
import { featuredSnak, shelfSnaks, shelfThumb, stationFilters, stationPillars, stationScreenshot, stationSource } from '../../../data/deck';
import type { ShelfSnak, StationFilter } from '../../../data/deck';
export function StationSection() {
  const [filter, setFilter] = useState<StationFilter>('All');
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return shelfSnaks.filter(snak => (filter === 'All' || snak.category === filter) && (!q || `${snak.title} ${snak.category}`.toLowerCase().includes(q)));
  }, [filter, query]);
  return <Section id="station" data-id="element-605" data-mp="3pqntpl1xwz0w">
      <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16" data-id="element-606" data-mp="3ptig3i4iktwy">
        <InView data-id="element-607" data-mp="3tr7ssu62qcpo">
          <SectionHeadline lead="Every Snak on one shelf." follow="Every market picks what works." data-id="element-608" data-mp="3tuvlxs7lwx3v" />
        </InView>
        <InView delay={0.1} data-id="element-609" data-mp="7lkm9toi4axb">
          <p className="text-[14.5px] leading-[1.7] text-[var(--muted)]" data-id="element-610" data-mp="7krmxdrvgnp7">
            Snakker Station stores every Snak in one place, so any Ferring country can see what is working elsewhere — then
            preview, reuse and localize it instead of starting over.
          </p>
        </InView>
      </div>

      <InView delay={0.16} y={26} className="mt-12" data-id="element-611" data-mp="3g0ukgqp9svzt">
        <div className="overflow-hidden rounded-[30px] bg-white shadow-[0_50px_90px_-60px_rgba(14,27,51,0.6)] ring-1 ring-[var(--fg)]/[0.08]" data-id="element-612" data-mp="3ksclferamwks">
          <div className="p-5 sm:p-7" data-id="element-613" data-mp="2nc5qrcqjnsy3">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-6" data-id="element-614" data-mp="2nb4ej4xsl0es">
              <div className="flex shrink-0 items-center gap-3" data-id="element-615" data-mp="2s9ywy5rukmyd">
                <span className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-[var(--brand)] text-white" data-id="element-616" data-mp="2kaas4cvkv3q0">
                  <PlayIcon className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-617" data-mp="2g9ifraresipi" />
                </span>
                <span className="text-[24px] font-extrabold leading-none tracking-[-0.03em] text-[var(--fg)]" data-id="element-618" data-mp="2u0dv95wexy22">Snakker Station</span>
              </div>
              <label className="flex flex-1 items-center gap-2.5 rounded-full bg-[var(--surface)] px-4 py-2.5 focus-within:ring-2 focus-within:ring-[var(--brand)]" data-id="element-619" data-mp="21bq1elxlm0cw">
                <SearchIcon className="h-4 w-4 text-[var(--fg)]/45" aria-hidden="true" data-id="element-620" data-mp="1xd95kyjmocas" />
                <span className="sr-only" data-id="element-621" data-mp="2b8kz52m1rwq4">Search Snaks</span>
                <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search — try “congress” or “follow-up”" className="w-full bg-transparent text-[13px] text-[var(--fg)] placeholder:text-[var(--fg)]/40 focus:outline-none" data-id="element-622" data-mp="26ajjysrqshjx" />
              </label>
              <span className="shrink-0 text-[12.5px] font-bold text-[var(--brand)]" data-id="element-623" data-mp="18j4of3xyvwj9">
                Discover. Preview. Reuse. Localize. Activate.
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2" data-id="element-624" data-mp="zgvhsp5tknb5">
              {stationFilters.map(item => <button key={item} type="button" onClick={() => setFilter(item)} aria-pressed={filter === item} className={['rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]', filter === item ? 'bg-[var(--fg)] text-white' : 'bg-[var(--surface)] text-[var(--fg)] hover:bg-[#EBE6DB]'].join(' ')} data-id="element-625" data-mp="1n80uwwkdl1hr">
                  {item}
                </button>)}
              <span className="ml-auto hidden text-[11.5px] text-[var(--muted)] md:inline" data-id="element-626" data-mp="mc0ufpibmfyj">
                {shelfSnaks.length + 1} Snaks · curated for global and local teams
              </span>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-[240px_minmax(0,0.8fr)_minmax(0,1.6fr)]" data-id="element-627" data-mp="dc5hbf57gavv">
              <div className="flex items-center justify-center rounded-[22px] bg-[#EFEBE2] px-4 py-5" data-id="element-628" data-mp="8d13gb7c5oz7">
                <SnakStill screen={0} alt="The REKOVELLE teaser Snak: two patients — same approach?" className="h-[380px] w-[196px]" data-id="element-629" data-mp="1531pw3k8ftdc" />
              </div>

              <div className="flex flex-col" data-id="element-630" data-mp="rac4o1cj2sha">
                <span className="w-fit rounded-full bg-[var(--brand)]/10 px-3 py-1 text-[11px] font-bold text-[var(--brand)]" data-id="element-631" data-mp="r9applse5lua">
                  {featuredSnak.category}
                </span>
                <h3 className="mt-3 text-[24px] font-extrabold leading-[1.05] tracking-[-0.03em] text-[var(--fg)]" data-id="element-632" data-mp="1jwwvn0p3d2kb">
                  {featuredSnak.title}
                </h3>
                <p className="mt-1.5 text-[12.5px] text-[var(--muted)]" data-id="element-633" data-mp="1w7xokkic66q0">{featuredSnak.usedIn}</p>
                <span className="mt-6 text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-634" data-mp="217k8tdwl0n8b">Helps solve</span>
                <p className="mt-1.5 text-[18px] font-bold leading-[1.25] text-[var(--fg)]" data-id="element-635" data-mp="25ayscejqblqi">{featuredSnak.solves}</p>
                <div className="mt-4 flex flex-wrap gap-1.5" data-id="element-636" data-mp="2a6e3uzu9l5jr">
                  {featuredSnak.channels.map(channel => <span key={channel} className="rounded-full border border-[var(--fg)]/20 px-3 py-1 text-[11.5px] font-medium text-[var(--fg)]" data-id="element-637" data-mp="2jybea4dg7thf">
                      {channel}
                    </span>)}
                </div>
                <span className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-[var(--fg)] px-4 py-2.5 text-[12.5px] font-bold text-white lg:mt-auto" data-id="element-638" data-mp="2xxji5akk3pry">
                  <SendIcon className="h-3.5 w-3.5 text-[var(--brand-lighter)]" aria-hidden="true" data-id="element-639" data-mp="xtqt1znpbi8t" />
                  Request adaptation
                </span>
              </div>

              <div data-id="element-640" data-mp="17n7jctomqf7q">
                <p className="text-[16px] font-extrabold text-[var(--fg)]" data-id="element-641" data-mp="17npm895srrom">
                  More on the shelf <span className="text-[var(--fg)]/40" data-id="element-642" data-mp="12stmsny8e21h">· {visible.length}</span>
                </p>
                <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-643" data-mp="1hlx3kt400br5">
                  <AnimatePresence mode="popLayout" data-id="element-644" data-mp="1ck7pf4lhbhyu">
                    {visible.map(snak => <motion.li key={snak.id} layout initial={{
                    opacity: 0,
                    scale: 0.96
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.96
                  }} transition={{
                    duration: 0.25
                  }} data-id="element-645" data-mp="1qjz29209esb9">
                        <ShelfThumb snak={snak} data-id="element-646" data-mp="22ugjhmug64bd" />
                        <p className="mt-2 text-[13px] font-bold leading-tight text-[var(--fg)]" data-id="element-647" data-mp="22wtbkstx5v25">{snak.title}</p>
                        <p className="mt-0.5 text-[11px] text-[var(--muted)]" data-id="element-648" data-mp="27vnqhjormzgz">{snak.category}</p>
                      </motion.li>)}
                  </AnimatePresence>
                </ul>
                {visible.length === 0 && <p className="mt-6 text-[13px] text-[var(--muted)]" data-id="element-649" data-mp="1k37cbk85frmy">No Snaks match that yet — try another filter.</p>}
              </div>
            </div>
          </div>

          <div className="grid gap-6 bg-[var(--fg)] px-5 py-7 sm:px-7 lg:grid-cols-[1fr_1fr_1fr_1.1fr] lg:items-center" data-id="element-650" data-mp="1b4c4y4tdcbi2">
            {stationPillars.map(pillar => <div key={pillar.label} data-id="element-651" data-mp="35hld9ta6hqyq">
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--brand-lighter)]" data-id="element-652" data-mp="35impi12xkji1">{pillar.label}</span>
                <p className="mt-1.5 text-[13px] leading-relaxed text-white/80" data-id="element-653" data-mp="39h2luvkwj484">{pillar.text}</p>
              </div>)}
            <p className="text-[19px] font-extrabold leading-[1.2] tracking-[-0.02em] text-white lg:text-right" data-id="element-654" data-mp="1sb0d2vdydw2v">
              One shared platform. Central intelligence. Local empowerment.
            </p>
          </div>
        </div>
      </InView>

      <WalkthroughInvite data-id="element-655" data-mp="1ike1rdd9rrpz" />
    </Section>;
}
function ShelfThumb({
  snak
}: {
  snak: ShelfSnak;
}) {
  return <div className="relative overflow-hidden rounded-[12px] bg-[var(--surface)]" style={{
    aspectRatio: `${shelfThumb.width} / ${shelfThumb.height}`
  }} data-id="element-656" data-mp="1ekn5wv8vylel">
      <img src={stationScreenshot} alt={`${snak.title} — ${snak.category} Snak thumbnail`} className="absolute block max-w-none" style={{
      width: `${stationSource.width / shelfThumb.width * 100}%`,
      left: `-${snak.x / shelfThumb.width * 100}%`,
      top: `-${snak.y / shelfThumb.height * 100}%`
    }} data-id="element-657" data-mp="14m7awh6qmrzt" />
    </div>;
}